import { useTranslation } from 'react-i18next';
import { FC, useState, useEffect } from 'react';
import { FormSelect, FormTextField } from '../../../../../lib/components';
import { Button } from '../../../../../lib/components/atomic/button';
import { Grid } from '@mui/material';
import { useStore } from '../../../../../hooks/useStore';
import { useFormikContext } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import {
  useCreatePaychexApiCredentials,
  useFetchPaychexApiCredentials,
  useFetchPaychexCompanies,
  useUpdatePaychexApiCredentials,
  PaychexApiCredentialsUpdateError
} from '../../../../../queries/company/paychex';

export const PaychexConfig: FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { companyStore } = useStore();

  const [apiUrl, setApiUrl] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [resetMode, setResetMode] = useState<boolean>(false);

  const companyId = companyStore.company?.id ?? 'MISSING_ID';

  const { values, setFieldValue } = useFormikContext<{
    paychex: { api_url: string; api_key: string; company: { value: string; text: string } | null };
  }>();

  const invalidatePaychexQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['company', companyId, 'paychex']
    });
  };

  const { data: paychexCompanies } = useFetchPaychexCompanies(companyId);
  const { data: paychexApiCredentials } = useFetchPaychexApiCredentials(companyId);

  // Set the current company when credentials and companies list are loaded
  useEffect(() => {
    if (paychexApiCredentials?.paychex_company_id && paychexCompanies?.companies) {
      const matchingCompany = paychexCompanies.companies.find(
        (c) => c.id === paychexApiCredentials.paychex_company_id
      );
      setFieldValue('paychex.company', {
        value: paychexApiCredentials.paychex_company_id,
        text: matchingCompany?.name ?? ''
      });
    }
  }, [paychexApiCredentials, paychexCompanies, setFieldValue]);

  const createPaychexApiCredentials = useCreatePaychexApiCredentials(companyId, {
    onSuccess: async () => {
      enqueueSnackbar(t('paychex:createCredentialsSuccess'), { variant: 'success' });
      invalidatePaychexQueries();
    },
    onError: () => {
      enqueueSnackbar(t('paychex:createCredentialsError'), {
        variant: 'error'
      });
    }
  });

  const updatePaychexApiCredentials = useUpdatePaychexApiCredentials(companyId, {
    onSuccess: () => {
      enqueueSnackbar(t('paychex:updateCredentialsSuccess'), { variant: 'success' });
      invalidatePaychexQueries();
    },
    onError: (error: PaychexApiCredentialsUpdateError) => {
      const errorDetails = error.body?.errors ? JSON.stringify(error.body.errors, null, 2) : '';
      enqueueSnackbar(t('paychex:updateCredentialsError_errors', { errors: errorDetails }), {
        variant: 'error'
      });
    }
  });

  useEffect(() => {
    if (paychexApiCredentials?.paychex_company_id) {
      setFieldValue('paychex.company', paychexApiCredentials?.paychex_company_id);
    }
  }, [paychexApiCredentials]);

  return (
    <>
      {!paychexApiCredentials?.id || resetMode ? (
        <>
          <FormTextField
            label={t('paychex:paychexApiUrl')}
            xs={3}
            name="paychex.api_url"
            additionalOnChange={(value) => setApiUrl(value as string)}
          />
          <FormTextField
            label={t('paychex:paychexApiKey')}
            xs={3}
            type="password"
            name="paychex.api_key"
            additionalOnChange={(value) => setApiKey(value as string)}
          />
          <Grid
            item
            xs={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              paddingBottom: '2px'
            }}
          >
            <Button
              variant="secondary"
              type="button"
              color="midnight"
              disabled={!values.paychex?.api_url || !values.paychex?.api_key}
              onClick={async () => {
                try {
                  if (!paychexApiCredentials?.id) {
                    await createPaychexApiCredentials.mutateAsync({
                      api_url: apiUrl,
                      api_key: apiKey
                    });
                  } else {
                    updatePaychexApiCredentials.mutate({
                      api_url: values.paychex.api_url,
                      api_key: values.paychex.api_key,
                      paychex_company_id: values.paychex.company?.value ?? '',
                      id: paychexApiCredentials.id
                    });
                  }
                } catch (error) {
                  enqueueSnackbar(
                    t('paychex:updateCredentialsError_errors', {
                      errors: JSON.stringify(error, null, 2)
                    }),
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setResetMode(false);
                }
              }}
            >
              {t('common:CONNECT')}
            </Button>
          </Grid>
        </>
      ) : (
        <>
          <Grid
            item
            xs={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              paddingBottom: '2px'
            }}
          >
            <Button
              variant="secondary"
              type="button"
              color="error"
              onClick={async () => {
                setFieldValue('paychex.api_url', undefined);
                setFieldValue('paychex.api_key', undefined);
                setFieldValue('paychex.company', undefined);
                setResetMode(true);
              }}
            >
              {t('paychex:resetCredentials')}
            </Button>
          </Grid>

          <FormSelect
            label={t('common:COMPANY')}
            xs={3}
            disabled={paychexCompanies?.companies.length === 0}
            name="paychex.company"
            options={
              paychexCompanies?.companies?.map((company) => ({
                value: company.id,
                text: company.name
              })) ?? []
            }
            aria-label="Paychex Company"
          />
        </>
      )}
    </>
  );
};
